/**
 * Bucket index into a 1024-based unit ladder.
 *
 * Clamped deliberately. For 0 < bytes < 1 the raw logarithm is negative, which
 * indexed the ladder out of bounds: `suffixes[-1]` is `undefined` and the value
 * was simultaneously scaled *up* by 1024, so a Chart.js axis tick of 0.9 rendered
 * as "921.60 undefined". Ticks on a chart with no data are exactly those
 * fractional values, which is where it showed. The top is clamped for the same
 * reason at the other end -- past YB the index runs off the ladder.
 */
function unitIndex(bytes: number, ladderLength: number): number {
  const raw = Math.floor(Math.log(Math.abs(bytes)) / Math.log(1024));
  return Math.min(ladderLength - 1, Math.max(0, raw));
}

/**
 * Format the value like a 'human-readable' file size (i.e. 13 KB, 4.1 MB,
 * 102 bytes, etc.).
 * @param givenBytes
 */
export function fileSizeFormat(
  givenBytes: number,
  places: number = 2,
  includeSuffix: boolean = true,
  autoTrimZero: boolean = true
) {
  const suffixes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  const getBytes = (bytes: number) => {
    const i = unitIndex(bytes, suffixes.length);
    if (!bytes) {
      return '0' + (includeSuffix ? ' Bytes' : '');
    }
    let converted = (bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : places);
    if (autoTrimZero) {
      converted = converted.replace(/\.0+$/g, '');
    }
    return converted + (includeSuffix ? ' ' + suffixes[i] : '');
  };

  return getBytes(givenBytes);
}

export function convertToBytes(size: string) {
  const suffixes = ['B', 'K', 'M', 'G', 'T', 'P', 'E', 'Z', 'Y'];
  let suffix = (size.match(/[a-zA-Z]/) || []).pop();
  if (!suffix) {
    return parseInt(size, 10);
  }

  const indexOfSuffix = size.indexOf(suffix);
  suffix = suffix.toUpperCase();

  const index = suffixes.indexOf(suffix);
  const parsed = parseInt(size.slice(0, indexOfSuffix), 10);

  return parsed * Math.pow(1024, index);
}

/**
 * Format the value like a 'human-readable' file size (i.e. 13 KB, 4.1 MB,
 * 102 bytes, etc.).
 * @param givenBytes
 */
export function networkFormat(
  givenBytes: number,
  places: number = 2,
  includeSuffix: boolean = true,
  autoTrimZero: boolean = true
) {
  const suffixes = ['bps', 'Kbps', 'Mbps', 'Gbps', 'Tbps', 'Pbps', 'Ebps', 'Zbps', 'Ybps'];
  const getBytes = (bytes: number) => {
    // Was `Math.abs(log(bytes) / log(1024))`, which avoided a negative index but
    // by reflecting it: 1e-7 bps landed on index 2 and rendered as "0 Mbps".
    // Clamping picks the bottom of the ladder instead.
    const i = unitIndex(bytes, suffixes.length);
    if (!bytes) {
      return '0' + (includeSuffix ? ' bps' : '');
    }
    let converted = (Math.abs(bytes) / Math.pow(1024, i)).toFixed(i === 0 ? 0 : places);
    if (autoTrimZero) {
      converted = converted.replace(/\.0+$/g, '');
    }
    return converted + (includeSuffix ? ' ' + suffixes[i] : '');
  };

  return getBytes(givenBytes);
}
